import { useState } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  { id: "identity",       label: "Identity",            description: "Government-issued photo ID for the individual or beneficial owner" },
  { id: "address",        label: "Address",             description: "Recent proof of address (utility bill, bank statement — last 90 days)" },
  { id: "wealth",         label: "Source of wealth",    description: "How was the capital you intend to commit accumulated?" },
  { id: "tax",            label: "Tax residency",       description: "Tax residency country and number (FATCA / CRS)" },
  { id: "ubo",            label: "UBO declaration",     description: "Beneficial ownership — required when investing through an entity" },
  { id: "classification", label: "Investor classification", description: "Confirmation you qualify as a Professional / HNW / Sophisticated investor under FCA rules" }
];

export default function PortalKyc() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState(null);
  const [form, setForm] = useState({
    fullLegalName: "",
    dateOfBirth: "",
    nationality: "",
    residentialAddress: "",
    sourceOfWealthText: "",
    taxResidencyCountry: "",
    taxIdNumber: "",
    classification: "professional"
  });
  const [files, setFiles] = useState({
    passport: null,
    proofOfAddress: null,
    sourceOfWealthDoc: null,
    uboDeclaration: null,
    classificationEvidence: null
  });

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }
  function setFile(field, file) {
    setFiles(prev => ({ ...prev, [field]: file }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Phase 3 wiring: build FormData with all fields + files, POST to /api/kyc/submit
      // Server parses multipart, uploads each file to LP's Box folder under /KYC/,
      // writes a kyc_submissions row, returns submission ID.
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      Object.entries(files).forEach(([k, f]) => { if (f) fd.append(k, f); });

      const res = await fetch("/api/kyc/submit", { method: "POST", body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Submission failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="portal-page">
        <h1 className="portal-h1">KYC submitted</h1>
        <section className="portal-card">
          <div className="empty-state">
            <p>
              Thanks — your KYC submission has been received. Daksfirst&rsquo;s
              compliance team will review within 1-2 business days. You&rsquo;ll receive
              an email once verified, and the status here will update.
            </p>
            <p className="muted small">
              If we need anything additional, we&rsquo;ll reach out via{" "}
              <a className="portal-link" href="mailto:info@daksfirst.com">info@daksfirst.com</a>.
            </p>
            <div className="hero-actions spacer-s">
              <Link to="/portal/onboarding/status" className="btn btn-primary">View status</Link>
              <Link to="/portal/pipeline" className="btn btn-ghost">Browse pipeline</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="portal-page">
      <h1 className="portal-h1">KYC onboarding</h1>
      <p className="muted">
        Six short sections. All documents are encrypted in transit and stored in your
        own Box folder, accessible only by you and Daksfirst&rsquo;s compliance team.
        Allow 5 minutes.
      </p>

      <form onSubmit={onSubmit} className="kyc-form">

        {/* IDENTITY */}
        <Section info={SECTIONS[0]}>
          <Field label="Full legal name" required>
            <input className="portal-input" required
                   value={form.fullLegalName}
                   onChange={e => set("fullLegalName", e.target.value)} />
          </Field>
          <Field label="Date of birth" required>
            <input className="portal-input" type="date" required
                   value={form.dateOfBirth}
                   onChange={e => set("dateOfBirth", e.target.value)} />
          </Field>
          <Field label="Nationality" required>
            <input className="portal-input" required
                   value={form.nationality}
                   onChange={e => set("nationality", e.target.value)} />
          </Field>
          <FileField label="Passport / national ID (PDF, JPG)" required
                     accept="application/pdf,image/*"
                     file={files.passport}
                     onSelect={f => setFile("passport", f)} />
        </Section>

        {/* ADDRESS */}
        <Section info={SECTIONS[1]}>
          <Field label="Residential address" required>
            <textarea className="portal-input" rows={3} required
                      value={form.residentialAddress}
                      onChange={e => set("residentialAddress", e.target.value)} />
          </Field>
          <FileField label="Proof of address (last 90 days)" required
                     accept="application/pdf,image/*"
                     file={files.proofOfAddress}
                     onSelect={f => setFile("proofOfAddress", f)} />
        </Section>

        {/* SOURCE OF WEALTH */}
        <Section info={SECTIONS[2]}>
          <Field label="Source of wealth — narrative" required>
            <textarea className="portal-input" rows={4} required
                      placeholder="e.g. proceeds from sale of business in 2018, ongoing income from listed equities, family inheritance"
                      value={form.sourceOfWealthText}
                      onChange={e => set("sourceOfWealthText", e.target.value)} />
          </Field>
          <FileField label="Supporting document (optional)"
                     accept="application/pdf"
                     file={files.sourceOfWealthDoc}
                     onSelect={f => setFile("sourceOfWealthDoc", f)} />
        </Section>

        {/* TAX */}
        <Section info={SECTIONS[3]}>
          <Field label="Tax residency country" required>
            <input className="portal-input" required
                   value={form.taxResidencyCountry}
                   onChange={e => set("taxResidencyCountry", e.target.value)} />
          </Field>
          <Field label="Tax identification number">
            <input className="portal-input"
                   value={form.taxIdNumber}
                   onChange={e => set("taxIdNumber", e.target.value)} />
          </Field>
        </Section>

        {/* UBO */}
        <Section info={SECTIONS[4]}>
          <FileField label="UBO declaration (only if investing through an entity)"
                     accept="application/pdf"
                     file={files.uboDeclaration}
                     onSelect={f => setFile("uboDeclaration", f)} />
          <p className="muted small">
            We can supply a template UBO declaration on request. Skip this section if
            investing in your personal name.
          </p>
        </Section>

        {/* CLASSIFICATION */}
        <Section info={SECTIONS[5]}>
          <Field label="Investor classification" required>
            <select className="portal-input"
                    value={form.classification}
                    onChange={e => set("classification", e.target.value)}>
              <option value="professional">Professional client (FCA)</option>
              <option value="hnw">High-net-worth individual (£250k+ net annual income or £500k+ net assets)</option>
              <option value="sophisticated">Self-certified sophisticated investor</option>
            </select>
          </Field>
          <FileField label="Supporting evidence (optional)"
                     accept="application/pdf"
                     file={files.classificationEvidence}
                     onSelect={f => setFile("classificationEvidence", f)} />
        </Section>

        {error && <p className="login-error">{error}</p>}

        <div className="hero-actions spacer-m">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit KYC"}
          </button>
          <Link to="/portal" className="btn btn-ghost">Save and exit</Link>
        </div>
      </form>
    </div>
  );
}

function Section({ info, children }) {
  return (
    <section className="portal-card kyc-section">
      <div className="kyc-section-head">
        <h2 className="portal-h2">{info.label}</h2>
        <p className="muted small">{info.description}</p>
      </div>
      <div className="kyc-section-body">{children}</div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="kyc-field">
      <label>{label}{required && <span className="req"> *</span>}</label>
      {children}
    </div>
  );
}

function FileField({ label, required, accept, file, onSelect }) {
  return (
    <div className="kyc-field">
      <label>{label}{required && <span className="req"> *</span>}</label>
      <div className="kyc-file">
        <input type="file"
               accept={accept}
               required={required && !file}
               onChange={e => onSelect(e.target.files?.[0] || null)} />
        {file && <span className="kyc-file-name">{file.name} ({Math.round(file.size / 1024)} KB)</span>}
      </div>
    </div>
  );
}
